import type {
  FloorPlannerCamera,
  FloorPlannerDesign,
  FloorPlannerItem,
  FloorPlannerPoint,
  FloorPlannerSurface,
} from '@repo/frontend-types';
import { isPointVisibleFromCamera } from '@repo/front-end-utils';

export interface CameraSight {
  /** Areas/zones with any sampled point inside the camera's view cone. */
  surfaces: FloorPlannerSurface[];
  /** Equipment whose centre point is inside the camera's view cone. */
  items: FloorPlannerItem[];
}

/** Ray-cast point-in-polygon. */
const pointInPolygon = (pt: FloorPlannerPoint, poly: FloorPlannerPoint[]): boolean => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    const aAbove = a.y > pt.y;
    const bAbove = b.y > pt.y;
    const crosses = aAbove !== bAbove && pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
};

/**
 * What a placed camera can see, using the same cone the canvas draws: radius =
 * effective distance, spread = viewing angle, centred on the facing direction.
 * Walls occlude along their centreline (openings and thickness are ignored —
 * good enough for a coverage checklist).
 *
 * The cone is `effectiveCoverage(camera)` — the marker's own numbers, or the default preset
 * where it has none — so a camera nobody has configured sees a believable default rather than
 * nothing (`camera-observer-setup-coverage`). The visibility test is `isPointVisibleFromCamera`
 * (`@repo/front-end-utils`), the same rule the plan's cone is drawn from; at 360° its half-angle
 * test admits every direction with no special case.
 */
export function getCameraSight(
  design: FloorPlannerDesign,
  camera: FloorPlannerCamera,
): CameraSight {
  const walls = design.walls ?? [];
  const camPt: FloorPlannerPoint = { x: camera.x, y: camera.y };
  // The one wall rule, shared with the cone the plan draws (`camera-feeds-page-plan-camera`).
  const isVisible = (pt: FloorPlannerPoint): boolean => isPointVisibleFromCamera(camera, walls, pt);

  // A surface counts when the camera stands inside it, or when any sampled
  // point of it (vertices, edge midpoints, vertex centroid) is visible.
  const surfaces = (design.surfaces ?? [])
    .filter(s => !s._deleted)
    .filter(s => {
      const poly = s.poly ?? [];
      if (poly.length === 0) return false;
      if (pointInPolygon(camPt, poly)) return true;

      const samples: FloorPlannerPoint[] = [...poly];
      for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        samples.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
      }
      samples.push({
        x: poly.reduce((acc, p) => acc + p.x, 0) / poly.length,
        y: poly.reduce((acc, p) => acc + p.y, 0) / poly.length,
      });
      return samples.some(isVisible);
    });

  const items = (design.items ?? [])
    .filter(i => !i._deleted)
    .filter(i => isVisible({ x: i.x, y: i.y }));

  return { surfaces, items };
}

/** The spaces and machines on a floor that no camera sees (refids). */
export interface SeenByNoCamera {
  spaces: ReadonlySet<string>;
  machines: ReadonlySet<string>;
}

const NOTHING_UNSEEN: SeenByNoCamera = { spaces: new Set(), machines: new Set() };

/**
 * The live spaces and machines no camera in `cameras` sees (`onboarding-status-colours-unseen`,
 * R1–R3): the floor's candidates minus the union of `getCameraSight` over every live camera. Built on
 * `getCameraSight`, not on the visibility test directly, so a space any camera sees part of is seen by
 * the same sampling the camera panel's "sees" line runs, and the mark, the panel and the cones cannot
 * disagree.
 *
 * A camera counts whether or not it is linked to a stream; a deleted one does not. With no live camera
 * nothing is unseen (R2): a floor nobody has started covering shows no marks. A space with no `poly`,
 * which no rule can see, is not a candidate; nor is an entity with no `refid`, which nothing can mark.
 */
export function getSeenByNoCamera(
  design: FloorPlannerDesign,
  cameras: readonly FloorPlannerCamera[],
): SeenByNoCamera {
  const live = cameras.filter(c => !c._deleted);
  if (live.length === 0) return NOTHING_UNSEEN;

  const seenSpaces = new Set<string>();
  const seenMachines = new Set<string>();
  for (const camera of live) {
    const sight = getCameraSight(design, camera);
    sight.surfaces.forEach(s => seenSpaces.add(s.refid));
    sight.items.forEach(i => seenMachines.add(i.refid));
  }

  const spaces = new Set<string>();
  (design.surfaces ?? []).forEach(s => {
    if (s._deleted || !s.refid || (s.poly ?? []).length === 0) return;
    if (!seenSpaces.has(s.refid)) spaces.add(s.refid);
  });
  const machines = new Set<string>();
  (design.items ?? []).forEach(i => {
    if (i._deleted || !i.refid) return;
    if (!seenMachines.has(i.refid)) machines.add(i.refid);
  });
  return { spaces, machines };
}
